import { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload as UploadIcon, FileSpreadsheet, Download, FileDown, Loader2, Plus, Replace, FolderOpen, CheckCircle2, Circle, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IndustryComboBox } from '@/components/IndustryComboBox';
import { toast } from 'sonner';
import { ContextualHelp } from '../components/ContextualHelp';
import { PreflightReport } from '../components/PreflightReport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Upload = ({ token, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cleansing, setCleansing] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [classifyProgress, setClassifyProgress] = useState(null);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [cleansingResult, setCleansingResult] = useState(null);
  const [classificationResult, setClassificationResult] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [taxonomy, setTaxonomy] = useState('custom');
  const [uploadMode, setUploadMode] = useState('new');
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [industry, setIndustry] = useState('');
  const [taxonomies, setTaxonomies] = useState([]);
  const [selectedTaxonomyId, setSelectedTaxonomyId] = useState('');
  // Pipeline tracking: 'idle' | 'uploading' | 'cleansing' | 'classifying' | 'recommendations' | 'complete' | 'error'
  const [pipelinePhase, setPipelinePhase] = useState('idle');
  const [pipelineError, setPipelineError] = useState(null);
  const [columnMapping, setColumnMapping] = useState(null);
  // Duplicate detection confirmation modal
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  // Preflight validation (step 2 of upload)
  const [checking, setChecking] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [autofix, setAutofix] = useState(true);
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (uploadMode !== 'new') fetchSessions();
  }, [uploadMode]);

  useEffect(() => {
    if (uploadMode === 'new') return;
    const s = sessions.find(x => x.id === selectedSessionId);
    if (s?.category_mode === 'keep_existing') setTaxonomy('keep_existing');
  }, [selectedSessionId, sessions, uploadMode]);

  useEffect(() => {
    const fetchTaxonomies = async () => {
      try {
        const res = await axios.get(`${API}/taxonomy`, { headers: { Authorization: `Bearer ${token}` } });
        setTaxonomies(res.data.taxonomies || []);
      } catch { /* ignore */ }
    };
    fetchTaxonomies();
  }, [token]);

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const res = await axios.get(`${API}/spend/processing-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(res.data.sessions || []);
      if (res.data.sessions?.length > 0 && !selectedSessionId) {
        setSelectedSessionId(res.data.sessions[0].id);
      }
    } catch { /* ignore */ } finally { setLoadingSessions(false); }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setUploadResult(null);
      setCleansingResult(null);
      setClassificationResult(null);
      setRecommendations(null);
      setPipelinePhase('idle');
      setPipelineError(null);
      setColumnMapping(null);
      setPreflight(null);
    }
    e.target.value = '';
  };

  const handlePreflight = async () => {
    if (!file) { toast.error('Please select a file first'); return; }
    if (file.size > 500 * 1024 * 1024) { toast.error('File too large. Maximum 500MB.'); return; }
    if (uploadMode !== 'new' && !selectedSessionId) { toast.error('Please select a session'); return; }
    setChecking(true);
    setPipelinePhase('idle');
    setPipelineError(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/spend/preflight`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }, timeout: 600000
      });
      setPreflight(res.data);
      setOverride(false);
      setOverrideReason('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'File check failed', { duration: 10000 });
    } finally { setChecking(false); }
  };

  const handleCancelPreflight = async (silent = false) => {
    try {
      await axios.delete(`${API}/spend/preflight/${preflight.preflight_id}`, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* already closed */ }
    setPreflight(null);
    if (!silent) toast.info('Upload cancelled. Nothing was stored — fix the file and check it again.');
  };

  const handleDownloadPreflightReport = async () => {
    try {
      const res = await axios.get(`${API}/spend/preflight/${preflight.preflight_id}/report.csv`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${preflight.filename.replace(/\.[^.]+$/, '')}_preflight_report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { toast.error('Report download failed'); }
  };

  const handleUpload = async (force = false) => {
    setConfirming(true);
    setPipelineError(null);
    setCleansingResult(null);
    setClassificationResult(null);
    setRecommendations(null);
    try {
      const response = await axios.post(`${API}/spend/preflight/${preflight.preflight_id}/confirm`, {
        mode: uploadMode, upload_id: uploadMode === 'new' ? null : selectedSessionId, industry,
        autofix, override, override_reason: overrideReason, force,
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 600000 });

      // Duplicate detection — backend returns 200 with success:false, duplicate_warning:true
      if (response.data?.duplicate_warning) {
        setDuplicateWarning({
          overlap_percentage: response.data.overlap_percentage,
          overlap_records: response.data.overlap_records,
          total_incoming: response.data.total_incoming,
          message: response.data.message,
        });
        return;
      }

      setPreflight(null);
      setUploading(true);
      setPipelinePhase('uploading');
      setUploadResult(response.data);
      setColumnMapping(response.data.column_mapping || null);
      toast.success(`Upload complete — ${response.data.records_inserted || response.data.new_records_inserted || 0} records ingested`);
      setUploading(false);
      setPipelinePhase('cleansing');
      await handleCleanse(response.data.upload_id);
    } catch (error) {
      let msg = 'Upload failed';
      if (error.code === 'ECONNABORTED') msg = 'Upload timeout. Try splitting into smaller files.';
      else if (error.response?.data?.detail) msg = error.response.data.detail;
      toast.error(msg, { duration: 10000 });
      if (!preflight) { setPipelinePhase('error'); setPipelineError(msg); }
    } finally {
      setConfirming(false);
      setUploading(false);
    }
  };

  const handleConfirmDuplicateUpload = async () => {
    setDuplicateWarning(null);
    await handleUpload(true);
  };

  const handleCancelDuplicateUpload = () => {
    setDuplicateWarning(null);
    handleCancelPreflight(true);
    setPipelinePhase('idle');
    toast.info('Upload cancelled. No duplicate records added.');
  };

  const handleCleanse = async (uploadId) => {
    setCleansing(true);
    try {
      const response = await axios.post(`${API}/spend/cleanse/${uploadId}`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      setCleansingResult(response.data);
      toast.success('Data cleansed successfully!');
      setPipelinePhase('classifying');
      await handleClassify(uploadId);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Cleansing failed';
      toast.error(msg);
      setPipelinePhase('error');
      setPipelineError(msg);
    } finally { setCleansing(false); }
  };

  const handleClassify = async (uploadId) => {
    setClassifying(true);
    try {
      let classifyUrl = `${API}/spend/classify/${uploadId}?taxonomy=${taxonomy}`;
      if (taxonomy === 'bespoke' && selectedTaxonomyId) {
        classifyUrl += `&taxonomy_id=${selectedTaxonomyId}`;
      }
      const response = await axios.post(classifyUrl, {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 600000 });

      if (response.data.async) {
        toast.info(response.data.message, { duration: 5000 });
        await pollClassificationStatus(uploadId);
      } else {
        setClassificationResult(response.data);
        toast.success('Data classified successfully!');
        setPipelinePhase('recommendations');
        await fetchRecommendations(uploadId);
        if (onUploadSuccess) onUploadSuccess();
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Classification failed';
      toast.error(msg);
      setPipelinePhase('error');
      setPipelineError(msg);
    } finally { setClassifying(false); }
  };

  const pollClassificationStatus = async (uploadId) => {
    const maxAttempts = 600;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await axios.get(`${API}/spend/classify-status/${uploadId}`,
          { headers: { Authorization: `Bearer ${token}` } });
        const { status, progress, total, percentage, error } = res.data;
        setClassifyProgress({ progress, total, percentage });

        if (status === 'classified') {
          setClassifyProgress(null);
          setClassificationResult({
            success: true,
            records_classified: res.data.records_classified || total,
            average_confidence: res.data.average_confidence || 0
          });
          toast.success(`Classification complete! ${res.data.records_classified || total} records classified.`);
          setPipelinePhase('recommendations');
          await fetchRecommendations(uploadId);
          if (onUploadSuccess) onUploadSuccess();
          return;
        } else if (status === 'classification_failed') {
          setClassifyProgress(null);
          const msg = `Classification failed: ${error || 'Unknown error'}`;
          toast.error(msg);
          setPipelinePhase('error');
          setPipelineError(msg);
          return;
        } else if (status === 'budget_exhausted') {
          setClassifyProgress(null);
          const msg = error || 'LLM budget exhausted — classification paused. Top up your Emergent Universal Key (Profile → Universal Key → Add Balance) and resume.';
          toast.error(msg, { duration: 10000 });
          setPipelinePhase('error');
          setPipelineError(msg);
          return;
        }
      } catch (err) {
        // Continue polling on network errors
      }
    }
    setClassifyProgress(null);
    toast.error('Classification is taking longer than expected. Check Processing Results for status.');
  };

  const fetchRecommendations = async (uploadId) => {
    setLoadingRecommendations(true);
    try {
      const response = await axios.post(`${API}/spend/recommendations/${uploadId}`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      setRecommendations(response.data);
      setPipelinePhase('complete');
    } catch {
      // Non-critical — still mark complete
      setPipelinePhase('complete');
    } finally { setLoadingRecommendations(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/spend/template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'procureaide_template_minimum.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Minimum template downloaded!');
    } catch { toast.error('Template download failed'); }
  };

  const handleDownloadRecommendedTemplate = async () => {
    try {
      const response = await axios.get(`${API}/spend/template/recommended`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'procureaide_template_recommended.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Recommended template downloaded!');
    } catch { toast.error('Template download failed'); }
  };

  const isProcessing = uploading || cleansing || classifying || checking;
  const selectedSession = sessions.find(s => s.id === selectedSessionId);

  return (
    <div className="space-y-6" data-testid="upload-page">
      <ContextualHelp guideId="preflight" />
      <div>
        <h1 className="text-3xl font-heading font-bold text-[#0F172A] mb-2">Data Cleansing & Classification</h1>
        <p className="text-slate-600">Upload raw spend data for AI-powered cleansing and classification</p>
      </div>

      <UploadSteps step={preflight ? 2 : (pipelinePhase !== 'idle' ? 3 : 1)} />

      {preflight && (
        <PreflightReport report={preflight}
          notice={taxonomy === 'keep_existing' && !Object.values(preflight.column_mapping?.mapped || {}).some(t => String(t).startsWith('category_l'))
            ? 'You chose "Keep my categories", but no L1-L4 category columns were found in this file. Every row will be classified by AI. Rename your columns (e.g. "Category L1"-"Category L4") or pick another taxonomy.'
            : null}
          autofix={autofix} setAutofix={setAutofix}
          override={override} setOverride={setOverride} reason={overrideReason} setReason={setOverrideReason}
          busy={confirming} onConfirm={() => handleUpload(false)} onCancel={() => handleCancelPreflight()}
          onDownload={handleDownloadPreflightReport} />
      )}

      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${preflight ? 'hidden' : ''}`}>
        <div className="space-y-6">
          {/* Upload Mode Selector */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Upload Mode</h3>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'new', label: 'New Session', icon: Plus, desc: 'Create a fresh dataset' },
                { id: 'append', label: 'Add to Existing', icon: FolderOpen, desc: 'Append rows to a session' },
                { id: 'replace', label: 'Replace Existing', icon: Replace, desc: 'Replace all data in a session' },
              ].map(m => (
                <button key={m.id} onClick={() => { setUploadMode(m.id); setUploadResult(null); }}
                  data-testid={`upload-mode-${m.id}`}
                  className={`p-3 border-2 rounded-sm text-left transition-all ${
                    uploadMode === m.id ? 'border-[#2563EB] bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                  }`}>
                  <m.icon size={18} className={uploadMode === m.id ? 'text-[#2563EB] mb-1.5' : 'text-slate-400 mb-1.5'} />
                  <p className="text-xs font-semibold text-slate-900">{m.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{m.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Session Selector (for append/replace) */}
          {uploadMode !== 'new' && (
            <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="session-selector-panel">
              <h3 className="text-sm font-semibold text-[#0F172A] mb-3">
                {uploadMode === 'append' ? 'Add data to' : 'Replace data in'}
              </h3>
              {loadingSessions ? (
                <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading sessions...</div>
              ) : sessions.length === 0 ? (
                <p className="text-sm text-slate-500">No existing sessions. Use "New Session" to create one first.</p>
              ) : (
                <select value={selectedSessionId} onChange={e => setSelectedSessionId(e.target.value)}
                  data-testid="existing-session-select"
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {sessions.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.filename} — {s.total_records} records ({s.status})
                    </option>
                  ))}
                </select>
              )}
              {selectedSession && (
                <div className="mt-3 p-3 bg-slate-50 rounded-sm text-xs text-slate-600 space-y-1">
                  <p><span className="font-medium">File:</span> {selectedSession.filename}</p>
                  <p><span className="font-medium">Records:</span> {selectedSession.total_records.toLocaleString()}</p>
                  <p><span className="font-medium">Status:</span> {selectedSession.status}</p>
                  {uploadMode === 'replace' && (
                    <p className="text-amber-600 font-medium mt-1">All existing data in this session will be permanently deleted and replaced.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* File Upload */}
          <div className="bg-white border border-slate-200 rounded-sm p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-sm mb-4">
                <FileSpreadsheet className="text-[#2563EB]" size={32} />
              </div>
              <h2 className="text-xl font-heading font-semibold text-[#0F172A] mb-2">Select File</h2>
              <p className="text-sm text-slate-500 mb-6">Supported formats: CSV, Excel (.xls, .xlsx), JSON</p>

              <div className="mb-4 grid grid-cols-2 gap-2">
                <Button data-testid="download-template-button" onClick={handleDownloadTemplate}
                  variant="outline" className="border-2 border-[#2563EB] text-[#2563EB] hover:bg-blue-50 font-medium gap-2">
                  <FileDown size={18} /> Minimum (CSV)
                </Button>
                <Button data-testid="download-recommended-template-button" onClick={handleDownloadRecommendedTemplate}
                  variant="outline" className="border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-medium gap-2"
                  title="14 fields incl. invoice_id, po_number, gl_account, cost_center — better classification & dedup">
                  <FileDown size={18} /> Recommended (XLSX)
                </Button>
              </div>
              <p className="text-xs text-slate-500 -mt-2 mb-4 text-left">
                <strong className="text-emerald-700">Tip:</strong> Only supplier, description and amount drive the AI. The Recommended template's optional fields (GL account, cost center, PO number, invoice ID) are kept as-is in your processed data and enable invoice-level dedup — they never influence cleansing or classification.
              </p>

              <div className="mb-6">
                <input type="file" id="file-upload" data-testid="file-upload-input"
                  accept=".csv,.xls,.xlsx,.json" onChange={handleFileChange} className="hidden" />
                <label htmlFor="file-upload"
                  className="inline-block px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-sm cursor-pointer transition-colors duration-200">
                  Choose File
                </label>
              </div>

              {file && (
                <div className="mb-6 p-4 bg-slate-50 rounded-sm border border-slate-200">
                  <p className="text-sm font-medium text-slate-700">{file.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              )}

              {file && (
                <div className="mb-6 p-5 bg-emerald-50 border border-emerald-200 rounded-sm text-left">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-1">Source Industry</h3>
                  <p className="text-xs text-slate-600 mb-3">AI uses industry context to improve cleansing accuracy and classification relevance</p>
                  <IndustryComboBox token={token} value={industry} onChange={setIndustry} />
                </div>
              )}

              {file && (
                <div className="mb-6 p-6 bg-blue-50 border border-blue-200 rounded-sm text-left">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Classification Taxonomy</h3>
                  <p className="text-xs text-slate-600 mb-4">Select how you want your spend data to be classified</p>
                  <div className="space-y-3">
                    {[
                      { value: 'custom', label: 'AI Generated L1-L4', desc: 'AI classifies using common business categories with L1-L4 depth hierarchy.' },
                      { value: 'unspsc', label: 'UNSPSC (United Nations Standard)', desc: 'UNSPSC codes with Segment, Family, Class, Commodity levels.' },
                      { value: 'reclassify_unspsc', label: 'Reclassify to UNSPSC', desc: 'Map existing custom categories to UNSPSC. Requires "category" column.', warn: true },
                      { value: 'bespoke', label: 'Bespoke Taxonomy', desc: 'Classify using your own custom category hierarchy created in Category Taxonomy.' },
                      { value: 'keep_existing', label: 'Keep my categories', desc: 'Use the L1-L4 categories already in your file. Fully categorised rows skip AI; the AI fills only missing levels and classifies uncategorised rows using your vocabulary.', hint: 'Needs columns like "Category L1"-"Category L4", "L1"-"L4" or "Level 1"-"Level 4".' },
                    ].map(t => (
                      <label key={t.value} data-testid={`taxonomy-${t.value.replace('_','-')}-option`}
                        className={`flex items-start p-4 border-2 rounded-sm cursor-pointer transition-all ${
                          taxonomy === t.value ? 'border-[#2563EB] bg-white shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}>
                        <input type="radio" name="taxonomy" value={t.value} checked={taxonomy === t.value}
                          onChange={e => { setTaxonomy(e.target.value); if (e.target.value !== 'bespoke') setSelectedTaxonomyId(''); }} className="mt-1 mr-3" />
                        <div className="flex-1">
                          <div className="font-semibold text-sm text-slate-900">{t.label}</div>
                          <div className="text-xs text-slate-600 mt-1">{t.desc}</div>
                          {t.warn && <div className="text-xs text-amber-600 mt-1 font-medium">Requires a "category" column in your uploaded file.</div>}
                          {t.hint && <div className="text-xs text-emerald-700 mt-1 font-medium">{t.hint}</div>}
                          {t.value === 'bespoke' && taxonomy === 'bespoke' && (
                            taxonomies.length > 0 ? (
                              <select value={selectedTaxonomyId} onChange={e => setSelectedTaxonomyId(e.target.value)}
                                onClick={e => e.stopPropagation()}
                                className="mt-2 w-full px-3 py-2 border border-blue-300 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                data-testid="bespoke-taxonomy-select">
                                <option value="">Select a taxonomy...</option>
                                {taxonomies.map(tx => (
                                  <option key={tx.id} value={tx.id}>{tx.name} ({(tx.categories || []).length} categories)</option>
                                ))}
                              </select>
                            ) : (
                              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-700" data-testid="no-bespoke-msg">
                                No bespoke taxonomies available. Create one in <span className="font-semibold">Category Taxonomy</span> first.
                              </div>
                            )
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <Button data-testid="upload-process-button" onClick={handlePreflight}
                disabled={!file || isProcessing || (uploadMode !== 'new' && !selectedSessionId) || (taxonomy === 'bespoke' && !selectedTaxonomyId)}
                className="w-full bg-[#0F172A] hover:bg-[#1e293b] text-white font-medium tracking-wide h-11">
                {checking ? (
                  <span className="flex items-center justify-center gap-2"><Loader2 size={18} className="animate-spin" /> Checking file...</span>
                ) : uploading ? 'Uploading...' : cleansing ? 'Cleansing Data...' : classifying ? (
                  classifyProgress ? `Classifying... ${classifyProgress.progress}/${classifyProgress.total} (${classifyProgress.percentage}%)` : 'Starting Classification...'
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <UploadIcon size={18} />
                    Check file
                  
                  </span>
                )}
              </Button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-sm p-6">
            <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Required Columns</h3>
            <ul className="space-y-2 text-sm text-slate-600 mb-4">
              {['supplier — Supplier name', 'description — Item/service description', 'amount — Transaction amount', 'date — Transaction date'].map(c => (
                <li key={c} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#2563EB] rounded-full" />
                  <span><strong>{c.split(' — ')[0]}</strong> — {c.split(' — ')[1]}</span>
                </li>
              ))}
            </ul>
            <div className="pt-3 border-t border-blue-200 space-y-2">
              <p className="text-xs text-slate-600">
                <strong>Auto-mapping enabled:</strong> Common ERP headers like SUPPLIER_NAME, INVOICE_DESCRIPTION, PAYMENT_AMOUNT, CURRENCY_CODE are automatically detected and mapped.
              </p>
              <p className="text-xs text-slate-600">Handles up to 1M records per upload. Max file size: 500MB.</p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Live Pipeline Progress Tracker */}
          {pipelinePhase !== 'idle' && (
            <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="pipeline-tracker">
              <h3 className="text-lg font-heading font-semibold text-[#0F172A] mb-1">Processing Pipeline</h3>
              <p className="text-xs text-slate-500 mb-5">Your data is being processed through the AI pipeline</p>

              <div className="space-y-0">
                {[
                  {
                    key: 'uploading',
                    label: 'Upload & Parse',
                    detail: uploadResult
                      ? `${(uploadResult.records_inserted || uploadResult.new_records_inserted || 0).toLocaleString()} records ingested`
                      : 'Parsing file and ingesting records...',
                    phases: ['uploading'],
                  },
                  {
                    key: 'cleansing',
                    label: 'AI Cleansing',
                    detail: cleansingResult
                      ? `${cleansingResult.duplicates_found || 0} duplicates, ${cleansingResult.records_cleansed || 0} records cleansed`
                      : 'Normalizing suppliers, detecting duplicates...',
                    phases: ['cleansing'],
                  },
                  {
                    key: 'classifying',
                    label: 'AI Classification',
                    detail: classificationResult
                      ? `${(classificationResult.records_classified || 0).toLocaleString()} records classified (${Math.round((classificationResult.average_confidence || 0) * 100)}% avg confidence)`
                      : classifyProgress
                        ? `Processing ${classifyProgress.progress.toLocaleString()} of ${classifyProgress.total.toLocaleString()} records`
                        : 'Starting classification...',
                    phases: ['classifying'],
                    progress: classifyProgress,
                  },
                  {
                    key: 'recommendations',
                    label: 'AI Recommendations',
                    detail: recommendations
                      ? 'Data quality insights generated'
                      : 'Generating data improvement recommendations...',
                    phases: ['recommendations'],
                  },
                ].map((step, idx, arr) => {
                  const phaseOrder = ['uploading', 'cleansing', 'classifying', 'recommendations', 'complete'];
                  const currentIdx = phaseOrder.indexOf(pipelinePhase);
                  const stepIdx = phaseOrder.indexOf(step.key);
                  const isComplete = currentIdx > stepIdx || pipelinePhase === 'complete';
                  const isActive = pipelinePhase === step.key;
                  const isPending = currentIdx < stepIdx && pipelinePhase !== 'error';
                  const isErrorOnThis = pipelinePhase === 'error' && stepIdx === currentIdx;

                  return (
                    <div key={step.key} data-testid={`pipeline-step-${step.key}`}>
                      <div className="flex items-start gap-3 py-3">
                        {/* Status icon */}
                        <div className="flex-shrink-0 mt-0.5">
                          {isComplete ? (
                            <CheckCircle2 size={22} className="text-emerald-500" />
                          ) : isActive ? (
                            <Loader2 size={22} className="text-[#2563EB] animate-spin" />
                          ) : isErrorOnThis || (pipelinePhase === 'error' && !isComplete) ? (
                            stepIdx <= phaseOrder.indexOf('uploading') || isErrorOnThis ? (
                              <AlertCircle size={22} className="text-red-500" />
                            ) : (
                              <Circle size={22} className="text-slate-300" />
                            )
                          ) : (
                            <Circle size={22} className="text-slate-300" />
                          )}
                        </div>
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm font-semibold ${isComplete ? 'text-emerald-700' : isActive ? 'text-[#2563EB]' : 'text-slate-400'}`}>
                              {step.label}
                            </p>
                            {isActive && step.progress && (
                              <span className="text-sm font-bold text-[#2563EB] bg-blue-50 px-2 py-0.5 rounded-full">
                                {step.progress.percentage}%
                              </span>
                            )}
                          </div>
                          <p className={`text-xs mt-0.5 ${isComplete ? 'text-emerald-600' : isActive ? 'text-slate-600' : 'text-slate-400'}`}>
                            {isComplete || isActive ? step.detail : 'Waiting...'}
                          </p>
                          {/* Progress bar for classification */}
                          {isActive && step.progress && (
                            <div className="mt-3" data-testid="classification-progress">
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-sm font-bold text-[#2563EB]">{step.progress.percentage}%</span>
                                <span className="text-xs text-slate-500">
                                  {step.progress.total - step.progress.progress > 0
                                    ? `~${Math.round((step.progress.total - step.progress.progress) / 60)} min remaining`
                                    : 'Finishing...'}
                                </span>
                              </div>
                              <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-[#2563EB] rounded-full transition-all duration-700 ease-out"
                                  style={{ width: `${step.progress.percentage || 0}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Connector line */}
                      {idx < arr.length - 1 && (
                        <div className="ml-[10px] w-0.5 h-2 bg-slate-200" />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Error banner */}
              {pipelinePhase === 'error' && pipelineError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-sm" data-testid="pipeline-error">
                  <p className="text-sm text-red-700 font-medium">{pipelineError}</p>
                </div>
              )}

              {/* Completion banner */}
              {pipelinePhase === 'complete' && (
                <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-sm" data-testid="pipeline-complete">
                  <p className="text-sm font-semibold text-emerald-800">All processing complete!</p>
                  <p className="text-xs text-emerald-700 mt-1">
                    View results in <strong>Processed Data</strong> or generate a report in <strong>Reports</strong>.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Column Mapping Summary */}
          {uploadResult && columnMapping && Object.keys(columnMapping.mapped || {}).length > 0 && (() => {
            // Only the essentials drive AI cleansing/classification. Every other
            // mapped column is preserved verbatim on the record (visible in
            // Processed Data + exports) but never sent to the LLM.
            const ESSENTIAL = ['supplier', 'description', 'amount'];
            const CORE_CONTEXT = ['currency', 'date']; // cleanse-only helpers
            const mappedEntries = Object.entries(columnMapping.mapped || {});
            const mappedTargets = new Set(mappedEntries.map(([, t]) => t));
            const essentialHit = ESSENTIAL.filter(f => mappedTargets.has(f));
            const keptAsIs = [...mappedTargets].filter(t => !ESSENTIAL.includes(t) && !CORE_CONTEXT.includes(t));
            const extrasCount = (columnMapping.unmapped || []).length;

            return (
              <div className="bg-white border border-blue-200 rounded-sm p-5" data-testid="column-mapping-result">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
                    <ArrowRight size={16} className="text-[#2563EB]" />
                    Column Mapping Applied
                  </h3>
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-sm border bg-emerald-50 text-emerald-700 border-emerald-200"
                    data-testid="upload-coverage-grade"
                  >
                    Essentials mapped ({essentialHit.length}/3) — ready for classification
                  </span>
                </div>

                <div className="space-y-1.5 mb-3">
                  {mappedEntries.map(([orig, target]) => (
                    <div key={orig} className="flex items-center text-xs gap-2">
                      <span className="text-slate-500 font-mono bg-slate-50 px-2 py-0.5 rounded">{orig}</span>
                      <ArrowRight size={12} className="text-slate-400 flex-shrink-0" />
                      <span className="text-[#2563EB] font-semibold">{target}</span>
                    </div>
                  ))}
                </div>

                {(keptAsIs.length > 0 || extrasCount > 0) && (
                  <p className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100" data-testid="upload-extras-note">
                    {keptAsIs.length > 0 && (
                      <><span className="font-mono">{keptAsIs.join(', ')}</span> — kept as-is in your processed data. </>
                    )}
                    {extrasCount > 0 && (
                      <>{extrasCount} additional column{extrasCount > 1 ? 's' : ''} stored as searchable extras. </>
                    )}
                    Only supplier, description and amount drive AI cleansing and classification.
                  </p>
                )}
              </div>
            );
          })()}

          {/* Upload Result Summary */}
          {uploadResult && (
            <div className="bg-white border border-green-200 rounded-sm p-6" data-testid="upload-result">
              <h3 className="text-lg font-heading font-semibold text-green-800 mb-2 flex items-center gap-2">
                <CheckCircle2 size={20} className="text-green-600" />
                {uploadMode === 'replace' ? 'Data Replaced' : uploadMode === 'append' ? 'Data Appended' : 'Upload Successful'}
              </h3>
              <div className="space-y-1 text-sm text-slate-700">
                {uploadResult.records_inserted > 0 && (
                  <p>Records ingested: <strong>{uploadResult.records_inserted.toLocaleString()}</strong></p>
                )}
                {uploadResult.new_records_inserted > 0 && (
                  <p>New records added: <strong>{uploadResult.new_records_inserted.toLocaleString()}</strong></p>
                )}
                {uploadResult.records_deleted > 0 && (
                  <p>Records replaced: <strong>{uploadResult.records_deleted.toLocaleString()}</strong></p>
                )}
                {uploadResult.total_records > 0 && (
                  <p>Total in session: <strong>{uploadResult.total_records.toLocaleString()}</strong></p>
                )}
                {uploadResult.merge_rules_applied > 0 && (
                  <p>Supplier merge rules applied: <strong>{uploadResult.merge_rules_applied}</strong></p>
                )}
              </div>
            </div>
          )}

          {/* Static info — only shown before processing starts */}
          {pipelinePhase === 'idle' && (
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h3 className="text-lg font-heading font-semibold text-[#0F172A] mb-4">What Happens Next?</h3>
              <div className="space-y-4">
                {[
                  { n: '0', color: 'slate', title: 'Preflight Check', desc: 'Every file is checked against data-hygiene rules first — nothing is stored until you confirm' },
                  { n: '1', color: 'blue', title: 'Upload & Parse', desc: 'Your file is uploaded and parsed into the database' },
                  { n: '2', color: 'green', title: 'AI Cleansing', desc: 'Data is normalized, suppliers are standardized, duplicates detected' },
                  { n: '3', color: 'purple', title: 'AI Classification', desc: `Spend is categorized using ${taxonomy === 'unspsc' ? 'UNSPSC codes' : taxonomy === 'reclassify_unspsc' ? 'UNSPSC reclassification' : taxonomy === 'bespoke' ? 'your bespoke taxonomy' : taxonomy === 'keep_existing' ? 'your own L1-L4 categories (AI fills only the gaps)' : 'AI-generated L1-L4 categories'}` },
                  { n: '4', color: 'orange', title: 'Results & Recommendations', desc: 'View detailed results, AI recommendations, and export cleansed data' },
                ].map(s => (
                  <div key={s.n} className="flex items-start gap-3">
                    <div className={`w-8 h-8 bg-${s.color}-50 rounded-full flex items-center justify-center flex-shrink-0`}>
                      <span className={`text-sm font-semibold text-${s.color}-600`}>{s.n}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{s.title}</p>
                      <p className="text-xs text-slate-600 mt-1">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-sm">
                <p className="text-sm text-slate-700">
                  <strong>Pro Tip:</strong> Use "Add to Existing" to combine multiple data sources into one session, or "Replace" to update a session with fresh data without creating duplicates.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Duplicate Upload Warning Modal */}
      {duplicateWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="duplicate-warning-modal">
          <div className="bg-white rounded-sm p-6 max-w-lg mx-4 shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle className="text-amber-600" size={20} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-heading font-semibold text-[#0F172A] mb-1">Possible Duplicate Upload</h3>
                <p className="text-sm text-slate-600">
                  {duplicateWarning.overlap_percentage}% of incoming records ({duplicateWarning.overlap_records?.toLocaleString()} of {duplicateWarning.total_incoming?.toLocaleString()}) appear to already exist in this session.
                </p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4">
              <p className="text-xs text-amber-800">
                <strong>Tip:</strong> If your previous upload completed but cleansing/classification stalled, cancel this and use the <strong>Retry</strong> buttons on the Processing Results page instead of re-uploading.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                onClick={handleCancelDuplicateUpload}
                variant="outline"
                data-testid="duplicate-cancel-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDuplicateUpload}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="duplicate-force-upload-button"
              >
                Upload Anyway
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const UploadSteps = ({ step }) => (
  <ol className="flex items-center gap-2 text-xs" data-testid="upload-steps">
    {['Check file', 'Review & confirm', 'Process'].map((label, i) => {
      const n = i + 1;
      const state = n < step ? 'done' : n === step ? 'active' : 'todo';
      return (
        <li key={label} className="flex items-center gap-2" data-testid={`upload-step-${n}-${state}`}>
          <span className={`w-6 h-6 rounded-full flex items-center justify-center font-semibold transition-colors duration-300 ${
            state === 'done' ? 'bg-emerald-500 text-white' : state === 'active' ? 'bg-[#0F172A] text-white' : 'bg-slate-100 text-slate-400'}`}>
            {state === 'done' ? <CheckCircle2 size={14} /> : n}
          </span>
          <span className={state === 'todo' ? 'text-slate-400' : 'text-slate-800 font-semibold'}>{label}</span>
          {n < 3 && <span className="w-8 h-px bg-slate-200 mx-1" />}
        </li>
      );
    })}
  </ol>
);
